<template>
  <div style="position:relative">
    <slot name="default"></slot>
    <v-dialog v-for="(item, index) in items" :key="'dialog-' + index" v-model="item.dialogActive" width="800px" persistent>
      <template v-slot:activator="{ on, attrs }">
        <v-list style="margin-bottom:1em">
          <v-list-item v-on="on" v-bind="attrs">
            <v-list-item-avatar>
              <v-icon left :color="getButtonColor(item)">{{ getButtonIcon(item) }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="index in confirmations && 'message' in confirmations[index] && confirmations[index].message !== ''" style="margin-top:1em;white-space:pre-wrap;">{{ confirmations[index].message }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="!item.viewed" style="margin-top:1em">{{ item.extraText.notViewed }}</v-list-item-subtitle>
              <v-list-item-subtitle v-else-if="item.isActive === 'Y'" style="margin-top:1em">{{ item.extraText.activated }}</v-list-item-subtitle>
              <v-list-item-subtitle v-else-if="activatable && item.isActive === 'N'" style="margin-top:1em">{{ item.extraText.deactivated }}</v-list-item-subtitle>
              <v-list-item-subtitle v-else style="margin-top:1em">{{ item.extraText.viewed }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
      <v-card>
        <v-card-text v-html="item.text" style="padding-top:20px"></v-card-text>
        <v-card-text v-if="activatable">
          <radio
            v-model="item.isActive"
            :label="item.actions.label"
            :items="[{ value: 'Y', text: item.actions.activate }, { value: 'N', text: item.actions.deactivate }]"
            :disabled="checkActiveDisabled(index)"></radio>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="viewed(index)">Close</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="item.actions.link" target="_blank" :href="item.actions.link" color="info" text>More Info</v-btn>
          <v-btn v-if="item.actions.contact" target="_blank" :href="'mailto:' + item.actions.contact" color="info" text>Contact</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { onBeforeUnmount, onMounted, ref } from '@vue/composition-api'
export default {
  props: {
    section: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    colWidth: {
      type: Number,
      required: true
    }
  },
  components: {
    Radio: () => import('@/components/forms/inputs/Radio')
  },
  setup (props, { root, emit }) {
    const items = ref([])
    const activatable = ref(false)
    const mustComplete = ref(false)
    const timeline = ref(false)
    const inOrder = ref(false)
    const content = ref('')

    const confirmations = ref([])
    const confirmationIndex = ref(-1)

    const trackServ = root.$feathers.service('orientation/tracker')
    const blockServ = root.$feathers.service('orientation/progress/block')
    const progressBlockId = ref(null)

    const onPatchedListener = (data) => { if (data._id === props.id) loadBlock() }
    onMounted(() => { root.$feathers.service('orientation/block').on('patched', onPatchedListener) })
    onBeforeUnmount(() => { root.$feathers.service('orientation/block').removeListener('patched', onPatchedListener) })

    async function loadBlock () {
      let data = await root.$feathers.service('orientation/block').get(props.id)
      for (let i = 0; i < data.items.length; i++) {
        data.items[i].dialogActive = false
        data.items[i].viewed = false
        if (data.activatable) {
          data.items[i].isActive = null
        }
        data.items[i].text = data.items[i].text.replace(/<a /ig, '<a target="_blank"')
        // Build blank entries for each confirmation; this will make things easier later so we know we have an entry for each item
        confirmations.value.push({ status: null, date: null })
      }
      activatable.value = data.activatable
      timeline.value = data.timeline
      inOrder.value = data.inOrder
      content.value = data.content
      items.value = data.items
      mustComplete.value = data.mustComplete
    }

    onMounted(async () => {
      await loadBlock()
      let { blocks } = await root.$store.dispatch('getTracker')
      for (let i = 0; i < blocks.length; i++) {
        if (blocks[i].section === props.section && JSON.stringify(blocks[i].block) === JSON.stringify(props.id)) {
          confirmationIndex.value = i
          if (blocks[i].items.length > 0) {
            confirmations.value = blocks[i].items
          } else {
            for (let i = 0; i < items.value.length; i++) {
              confirmations.value.push({})
            }
          }
          for (let j = 0; j < blocks[i].items.length; j++) {
            let temp = JSON.parse(JSON.stringify(items.value[j]))
            if (temp.isActive !== blocks[i].items[j].status) {
              temp.isActive = blocks[i].items[j].status
              items.value.splice(j, 1, temp)
            }
          }
          break
        }
      }
      if (confirmationIndex.value === -1) {
        let arr = []
        for (let i = 0; i < items.value.length; i++) {
          arr.push({})
        }
        if (root.$route.path.substr(0, 7) !== '/admin/') {
          trackServ.patch(root.$store.state.tracker.id, { $push: { blocks: { section: props.section, block: props.id, complete: !mustComplete.value, items: arr } } }).then(({ blocks }) => {
            confirmationIndex.value = blocks.length - 1
            root.$store.commit('setTrackerBlocks', blocks)
            confirmations.value = blocks[confirmationIndex.value].items
          })
          blockServ.find({ query: { pidm: root.$store.state.user.pidm, block: props.id } }).then(({ data }) => {
            if (data.length === 0) {
              blockServ.create({ pidm: root.$store.state.user.pidm, term: root.$store.state.term, section: props.section, block: props.id }).then((data) => {
                progressBlockId.value = data._id
              })
            } else {
              progressBlockId.value = data._id
            }
          })
        }
      }
      checkComplete()
    })

    function getButtonColor (item) {
      if (activatable.value) {
        if (item.isActive === 'Y') return 'success'
        else if (item.isActive === 'N') return 'error'
      } else if (item.viewed) {
        return ''
      }
      return 'info'
    }

    function getButtonIcon (item) {
      if (activatable.value) {
        if (item.isActive === 'Y') return 'fas fa-check-circle'
        else if (item.isActive === 'N') return 'fas fa-times-circle'
      } else if (item.viewed) {
        // viewed but not activatable will just give a check
        return 'fas fa-check-circle'
      }
      return 'fas fa-exclamation-circle'
    }

    function viewed (index) {
      let temp = items.value.splice(index, 1)[0]
      temp.viewed = true
      temp.dialogActive = false
      items.value.splice(index, 0, temp)

      // Track that this item was viewed (and the activated state, if it has one)
      if (temp.isActive != null) confirmations.value[index].status = temp.isActive
      confirmations.value[index].date = new Date()
      let patchIndex = confirmationIndex.value
      if (root.$route.path.substr(0, 7) !== '/admin/') {
        trackServ.patch(root.$store.state.tracker.id, { ['blocks.' + patchIndex + '.items.' + index]: confirmations.value[index] }).then(({ blocks }) => {
          root.$store.commit('setTrackerBlocks', blocks)
        })
        blockServ.patch(progressBlockId.value, { ['items.' + index]: confirmations.value[index] })
      }
      checkComplete()
    }

    function checkComplete () {
      if (mustComplete.value) {
        let completed = true
        for (const item of items.value) {
          if (activatable.value && item.isActive == null) completed = false
          else if (!activatable.value && !item.viewed) completed = false
        }
        if (completed) {
          emit('activeFinish')
        } else {
          emit('activeWaiting')
        }
      }
    }

    function isPrevCompleted (index) {
      if (index === 0) return true
      if (!inOrder.value) return true
      return items.value[index - 1].viewed
    }

    function checkActiveDisabled (index) {
      // If 'checkAdmin' is set on the confirmation block for the given index, then the student cannot change the status so disable the radio button
      return index in confirmations.value && 'adminCheck' in confirmations.value[index] && confirmations.value[index].adminCheck === true
    }

    return {
      items,
      activatable,
      mustComplete,
      timeline,
      inOrder,
      content,
      confirmations,
      progressBlockId,
      getButtonColor,
      getButtonIcon,
      viewed,
      checkComplete,
      isPrevCompleted,
      checkActiveDisabled
    }
  }
}
</script>
